import React, { useState, useRef, useEffect } from 'react';
import { eventService } from '../services/eventService';
import { boatService } from '../services/boatService';
import type { EventQASource, BoatInfo } from '../types';

/** Cached answer for repeated questions */
interface CachedAnswer {
  answer: string;
  sources: Array<{ documentType: string; sectionNumber: string }>;
  tokenUsage?: { input: number; output: number; total: number };
  cost?: number;
  queryTimeMs?: number;
  timing?: { loadSections: number; generateEmbedding: number; similaritySearch: number; claudeResponse: number; total: number };
}

interface EventHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  raceId: string;
  eventName: string;
}

/**
 * Modal for asking natural language questions about a specific event.
 * Combines context from Special Regulations and event documents (NoR, SI, Amendments).
 * Uses Claude via Bedrock to answer questions with citations.
 *
 * Requirements: 5.2, 5.3, 5.4, 5.5, 5.6, 5.7
 */
const EventHelpModal: React.FC<EventHelpModalProps> = ({
  isOpen,
  onClose,
  raceId,
  eventName,
}) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [sources, setSources] = useState<EventQASource[]>([]);
  const [tokenUsage, setTokenUsage] = useState<{ input: number; output: number; total: number } | null>(null);
  const [cost, setCost] = useState<number | null>(null);
  const [queryTimeMs, setQueryTimeMs] = useState<number | null>(null);
  const [timing, setTiming] = useState<{ loadSections: number; generateEmbedding: number; similaritySearch: number; claudeResponse: number; total: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boat, setBoat] = useState<BoatInfo | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const answerCacheRef = useRef<Map<string, CachedAnswer>>(new Map());

  // Cleanup on unmount: abort controller, clear cache, reset state
  useEffect(() => {
    const cache = answerCacheRef.current;
    return () => {
      abortControllerRef.current?.abort();
      cache.clear();
      setQuestion('');
      setAnswer(null);
      setSources([]);
      setTokenUsage(null);
      setCost(null);
      setQueryTimeMs(null);
      setTiming(null);
      setError(null);
      setLoading(false);
    };
  }, []);

  // Fetch boat info when modal opens
  useEffect(() => {
    if (isOpen) {
      boatService.getBoat().then(setBoat).catch(() => setBoat(null));
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    // Abort any previous in-flight request
    abortControllerRef.current?.abort();

    const trimmedQuestion = question.trim();

    // Check cache first (keyed by raceId:question)
    const cacheKey = `${raceId}:${trimmedQuestion}`;
    const cached = answerCacheRef.current.get(cacheKey);
    if (cached) {
      setAnswer(cached.answer);
      setSources(cached.sources);
      setTokenUsage(cached.tokenUsage ?? null);
      setCost(cached.cost ?? null);
      setQueryTimeMs(cached.queryTimeMs ?? null);
      setTiming(cached.timing ?? null);
      setError(null);
      return;
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setAnswer(null);
    setSources([]);
    setTokenUsage(null);
    setCost(null);
    setQueryTimeMs(null);
    setTiming(null);

    try {
      const boatContext = boat ? { hullType: boat.hullType, lengthMeters: boat.lengthMeters } : undefined;
      const startTime = performance.now();
      const result = await eventService.askEventQuestion(raceId, trimmedQuestion, boatContext, controller.signal);
      const elapsed = Math.round(performance.now() - startTime);
      setAnswer(result.answer);
      setSources(result.sources);
      if (result.tokenUsage) setTokenUsage(result.tokenUsage);
      if (result.cost !== undefined) setCost(result.cost);
      setQueryTimeMs(elapsed);
      if (result.timing) setTiming(result.timing);

      // Store in cache
      answerCacheRef.current.set(cacheKey, {
        answer: result.answer,
        sources: result.sources,
        tokenUsage: result.tokenUsage,
        cost: result.cost,
        queryTimeMs: elapsed,
        timing: result.timing,
      });
    } catch (err: any) {
      // Silently ignore AbortError / CanceledError
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.response?.data?.message || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Abort any in-flight request, but do NOT reset state
    abortControllerRef.current?.abort();
    onClose();
  };

  /** Format document type for display */
  const formatDocumentType = (docType: string): string => {
    switch (docType) {
      case 'notice_of_race':
        return 'Notice of Race';
      case 'sailing_instructions':
        return 'Sailing Instructions';
      case 'special_regulations':
        return 'Special Reg';
      case 'racing_rules':
        return 'Rule';
      case 'irc_rules':
        return 'IRC';
      case 'orci_rules':
        return 'ORCi';
      default:
        if (docType.startsWith('amendment_')) {
          const num = docType.split('_')[1];
          return `Amendment ${num}`;
        }
        return docType;
    }
  };

  /** Get CSS class for document type badge */
  const getDocTypeBadgeClass = (docType: string): string => {
    if (docType === 'notice_of_race') return 'badge-nor';
    if (docType === 'sailing_instructions') return 'badge-si';
    if (docType === 'special_regulations') return 'badge-sr';
    if (docType === 'racing_rules') return 'badge-rrs';
    if (docType === 'irc_rules') return 'badge-irc';
    if (docType === 'orci_rules') return 'badge-orci';
    if (docType.startsWith('amendment_')) return 'badge-amendment';
    return 'badge-default';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content event-help-modal">
        <div className="modal-header">
          <h2>Ask About {eventName}</h2>
          <button className="modal-close-btn" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit} className="event-question-form">
          <label htmlFor="event-question">
            Ask a question about this event's requirements:
          </label>
          <textarea
            id="event-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g., What are the entry requirements? When is the skipper's briefing?"
            rows={3}
            maxLength={500}
            disabled={loading}
          />
          <div className="form-actions">
            <button type="submit" disabled={loading || !question.trim()}>
              {loading ? 'Thinking...' : 'Ask'}
            </button>
          </div>
        </form>

        {error && <div className="error-message">{error}</div>}

        {answer && (
          <div className="event-answer">
            <h3>Answer</h3>
            <div className="answer-text">{answer}</div>
            {sources.length > 0 && (
              <div className="answer-sources">
                <strong>Sources:</strong>
                <div className="source-badges">
                  {sources.map((source, index) => (
                    <span
                      key={index}
                      className={`source-badge ${getDocTypeBadgeClass(source.documentType)}`}
                    >
                      {formatDocumentType(source.documentType)} §{source.sectionNumber}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        <div className="modal-footer">
          <p className="disclaimer">
            Answers are AI-generated based on uploaded event documents and Special Regulations.
            Always verify critical requirements against the official documents.
          </p>
          {tokenUsage && (
            <p className="token-usage" style={{ fontSize: '0.75rem', color: '#888', marginTop: '8px' }}>
              Tokens: {tokenUsage.input.toLocaleString()} in / {tokenUsage.output.toLocaleString()} out / {tokenUsage.total.toLocaleString()} total
              {cost !== null && ` • Cost: $${cost.toFixed(4)}`}
              {queryTimeMs !== null && ` • ${(queryTimeMs / 1000).toFixed(1)}s`}
            </p>
          )}
          {timing && (
            <p className="timing-breakdown" style={{ fontSize: '0.7rem', color: '#aaa', marginTop: '4px' }}>
              Load: {timing.loadSections}s • Embed: {timing.generateEmbedding}s • Search: {timing.similaritySearch}s • Claude: {timing.claudeResponse}s • Backend: {timing.total}s
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventHelpModal;
